import { useEffect, useState } from "react";
import { get } from "../../lib/api";
import { useSidebar } from "../ui/sidebar";
import { Input } from "../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useIsMobile } from "../../hooks/use-mobile";

const FeedbackTable = ({ game_id }) => {
  const { dates } = useSidebar();
  const isMobile = useIsMobile();
  const [data, setData] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [filterField, setFilterField] = useState("date");
  const [filterOperator, setFilterOperator] = useState("contains");
  const [filterValue, setFilterValue] = useState("");
  const [loading, setLoading] = useState(true);

  // Custom classes for gray rows and white border
  const tableRowClass = "bg-gray-50 dark:bg-gray-900 hover:bg-gray-100";
  const tableClass = "min-w-full bg-[#ffffff] dark:bg-[var(--header-color-dark)] border border-white dark:border-white ";

  useEffect(() => {
    const fetchData = async () => {
      if (!game_id) return;
      const feedback = await get(`/statistics?method=getFeedback&gameid=${game_id}&date=${dates[0]}_${dates[1]}`);

      if (Object.keys(feedback).length === 0) {
        setData([]);
        setLoading(false);
        return;
      }

      const rows = feedback.brand.map((_, index) => ({
        date: feedback.date[index],
        user_ID: feedback.user_ID[index],
        brand: feedback.brand[index],
        identifier: feedback.identifier[index],
        stars: feedback.stars[index],
        contact: feedback.contact[index],
        feedback: feedback.feedback[index],
      }));

      setData(rows);
      setLoading(false);
    };

    fetchData();
  }, [game_id, dates]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortArray = (array) => {
    return array.sort((a, b) => {
      if (a[orderBy] === "" || b[orderBy] === "") {
        if (a[orderBy] === "" && b[orderBy] !== "") {
          return 1; // a is empty, b is not, a should be at the bottom
        } else if (a[orderBy] !== "" && b[orderBy] === "") {
          return -1; // b is empty, a is not, b should be at the bottom
        }
        return 0; // both are empty, doesn't matter which comes first
      }

      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (aValue < bValue) {
        return order === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  const sortedData = sortArray([...data]);

  const applyFilter = (row) => {
    const value = row[filterField] ?? "";
    if (filterOperator === "contains") return String(value).includes(filterValue);
    if (filterOperator === "equals") return String(value) === String(filterValue);
    if (filterOperator === "startsWith") return String(value).startsWith(filterValue);
    if (filterOperator === "endsWith") return String(value).endsWith(filterValue);
    if (filterOperator === "isEmpty") return value === "" || value === 0;
    if (filterOperator === "isNotEmpty") return value !== "" && value !== 0;
  };

  const filteredData = sortedData.filter(applyFilter);

  if (loading) {
    return (
      <div className={`w-full overflow-x-auto p-4 ${isMobile ? 'px-2' : ''}`} style={{width: isMobile ? '100%' : '87.5vw'}}>
        <div className="bg-[#ffffff] rounded-lg p-4 dark:bg-[var(--header-color-dark)]">
          {/* Skeleton Filter Controls */}
          <div className={`grid ${isMobile ? 'grid-cols-1 gap-2' : 'grid-cols-1 md:grid-cols-3 gap-4'} mb-4`}>
            <div className="w-full h-10 bg-gray-300 dark:bg-gray-700 animate-pulse rounded-md"></div>
            <div className="w-full h-10 bg-gray-300 dark:bg-gray-700 animate-pulse rounded-md"></div>
            <div className="w-full h-10 bg-gray-300 dark:bg-gray-700 animate-pulse rounded-md"></div>
          </div>
          {/* Skeleton Table */}
          <div className="overflow-x-auto rounded-lg max-h-[500px]">
            <Table className={tableClass}>
              <TableHeader className="bg-gray-100 sticky dark:bg-purple-700">
                <TableRow>
                  {[
                    "Date", "User ID", "Brand", "Identifier", "Stars", "Contact", "Feedback"
                  ].map((headCell) => (
                    <TableHead key={headCell} className={`px-4 sticky py-2 ${isMobile ? 'px-2 py-1 text-xs' : ''}`}>
                      {headCell}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...Array(5)].map((_, index) => (
                  <TableRow key={index} className={tableRowClass + " animate-pulse"}>
                    {Array(7).fill().map((_, cellIdx) => (
                      <TableCell key={cellIdx} className={`px-4 py-3 ${isMobile ? 'px-2 py-1' : ''}`}>
                        <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded"></div>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full overflow-x-auto p-4 ${isMobile ? 'px-2' : ''}`} style={{width: isMobile ? '100%' : '87.5vw'}}>
      <div className={`bg-[#ffffff] rounded-lg shadow-md dark:bg-[#232833] dark:text-[#f3f4f6] ${isMobile ? 'p-2' : 'p-4'}`}>
        {/* Filters Section */}
        <div className={`grid ${isMobile ? 'grid-cols-1 gap-2' : 'grid-cols-1 md:grid-cols-3 gap-4'} mb-4`}>
          {/* Field Selection */}
          <Select value={filterField} onValueChange={setFilterField}>
            <SelectTrigger className={`w-full shadow-none focus:shadow-none bg-[#ffffff] dark:bg-[#2c313c] dark:text-white border border-gray-300 dark:border-gray-700 ${isMobile ? 'text-xs' : ''}`}>
              <SelectValue placeholder="Select Field" />
            </SelectTrigger>
            <SelectContent className="bg-[#ffffff] dark:bg-[#232833] dark:text-white border border-gray-300 dark:border-gray-700">
              {[
                "date",
                "user_ID",
                "brand",
                "identifier",
                "stars",
                "contact",
                "feedback",
              ].map((field) => (
                <SelectItem key={field} value={field} className="dark:bg-[#232833] dark:text-white">
                  {field.charAt(0).toUpperCase() + field.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Operator Selection */}
          <Select value={filterOperator} onValueChange={setFilterOperator}>
            <SelectTrigger className={`w-full shadow-none focus:shadow-none bg-[#ffffff] dark:bg-[#2c313c] dark:text-white border border-gray-300 dark:border-gray-700 ${isMobile ? 'text-xs' : ''}`}>
              <SelectValue placeholder="Select Operator" />
            </SelectTrigger>
            <SelectContent className="bg-[#ffffff] dark:bg-[#232833] dark:text-white border border-gray-300 dark:border-gray-700">
              {[
                "contains",
                "equals",
                "startsWith",
                "endsWith",
                "isEmpty",
                "isNotEmpty",
              ].map((op) => (
                <SelectItem key={op} value={op} className="dark:bg-[#232833] dark:text-white">
                  {op.replace(/([A-Z])/g, " $1").toLowerCase()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Input Filter */}
          <Input 
            value={filterValue} 
            onChange={(e) => setFilterValue(e.target.value)} 
            placeholder="Filter value" 
            className={`w-full shadow-none focus:shadow-none bg-[#ffffff] dark:bg-[#2c313c] dark:text-white border border-gray-300 dark:border-gray-700 ${isMobile ? 'text-xs' : ''}`} 
          />
        </div>

        {/* Table Section */}
        <div className="overflow-x-auto rounded-xl max-h-[500px]">
          <Table className="min-w-full bg-[#ffffff] dark:bg-[#232833] border border-white dark:border-[#2c313c]">
            <TableHeader className="bg-gray-100 sticky dark:bg-[#ffffff] dark:text-white">
              <TableRow>
                {[
                  "Date",
                  "User ID",
                  "Brand",
                  "Identifier",
                  "Stars",
                  "Contact",
                  "Feedback",
                ].map((headCell) => (
                  <TableHead
                    key={headCell}
                    className={`px-4 py-2 cursor-pointer hover:bg-gray-200 dark:hover:bg-[#35394a] dark:text-white text-black ${isMobile ? 'px-2 py-1 text-xs' : ''}`}
                    onClick={() => handleRequestSort(headCell === "User ID" ? "user_ID" : headCell.toLowerCase())}
                  >
                    {headCell}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody className="dark:bg-[#232833]">
              {filteredData.length > 0 ? (
                filteredData.map((row, index) => (
                  <TableRow key={index} className="bg-gray-50 dark:bg-[#2c313c] hover:bg-gray-100 dark:hover:bg-[#35394a] border-b border-gray-200 dark:border-[#35394a]">
                    <TableCell className={`px-4 py-2 dark:text-white min-w-[10rem] ${isMobile ? 'px-2 py-1 text-xs' : ''}`}>{row.date}</TableCell>
                    <TableCell className={`px-4 py-2 dark:text-white ${isMobile ? 'px-2 py-1 text-xs' : ''}`}>{row.user_ID}</TableCell>
                    <TableCell className={`px-4 py-2 dark:text-white ${isMobile ? 'px-2 py-1 text-xs' : ''}`}>{row.brand}</TableCell>
                    <TableCell className={`px-4 py-2 dark:text-white ${isMobile ? 'px-2 py-1 text-xs' : ''}`}>{row.identifier}</TableCell>
                    <TableCell className={`px-4 py-2 dark:text-white ${isMobile ? 'px-2 py-1 text-xs' : ''}`}>{row.stars}</TableCell>
                    <TableCell className={`px-4 py-2 dark:text-white ${isMobile ? 'px-2 py-1 text-xs' : ''}`}>{row.contact}</TableCell>
                    <TableCell className={`px-4 py-2 dark:text-white ${isMobile ? 'px-2 py-1 text-xs' : ''}`}>{row.feedback}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className={`text-center py-4 dark:text-white ${isMobile ? 'text-xs' : ''}`}>No feedback data available.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default FeedbackTable;
